/**
 * groupByArea — group line items by areaId while preserving insertion order.
 *
 * §358 (2026-08-19): the two builder pages (Quote + ChangeOrder) previously
 * built area groups with a plain `Object` and returned `Object.values(...)`.
 * That works for slug areaIds ('walls', 'options', ...) — non-integer strings
 * keep insertion order per JS spec. But migrated tenants carry INTEGER-LIKE
 * areaIds ("1381", "1382", ...), and `Object.values()` on those sorts them
 * ASCENDING NUMERICALLY. Every re-render silently re-sorted the areas,
 * discarding user-driven reorders and rendering areas in numeric-id order
 * instead of line-item order.
 *
 * A `Map` preserves insertion order for ALL key types. This helper is the
 * single definition (§5.2) that both builder pages call — swap the impl
 * here and both surfaces move in lockstep.
 *
 * The `groupFactory` argument builds the per-group value on first insert,
 * so callers with slightly different group shapes (Quote uses
 * `areaLabel`/`indices`; ChangeOrder uses `label`/`computedItems`) can
 * plug in their own initializer. `onItem(group, item, idx)` appends the
 * item into the group's internal collections.
 *
 * @param {Array}    items         line items in canonical order
 * @param {Function} groupFactory  (item, areaId) => {} — initial group shape
 * @param {Function} onItem        (group, item, idx) => void — append hook
 * @returns {Array}                groups in the order their first item appeared
 */
export function groupLinesByArea(items, groupFactory, onItem) {
  const groups = new Map();
  (items || []).forEach((item, idx) => {
    const areaId = (item && item.areaId) || 'uncategorized';
    if (!groups.has(areaId)) {
      groups.set(areaId, groupFactory(item, areaId));
    }
    onItem(groups.get(areaId), item, idx);
  });
  return [...groups.values()];
}
